<template>
  <div class="item-timeline" ref="containerEl">
    <!-- Header row: month labels -->
    <div class="timeline-months" :style="{ width: totalWidth + 'px' }">
      <div
        v-for="month in months"
        :key="month.label"
        class="timeline-month-label"
        :style="{ left: month.left + 'px', width: month.width + 'px' }"
      >{{ month.label }}</div>
    </div>

    <!-- Scrollable track -->
    <div class="timeline-track" :style="{ width: totalWidth + 'px', minHeight: trackHeight + 'px' }">

      <!-- Today line -->
      <div
        class="timeline-today-line"
        :style="{ left: todayX + 'px' }"
        :title="$t('common.today') + ': ' + fmtDate(today)"
      />

      <!-- Day column guides (subtle) -->
      <div
        v-for="week in weekGuides"
        :key="week"
        class="timeline-week-guide"
        :style="{ left: week + 'px' }"
      />

      <!-- Booking bars -->
      <div
        v-for="(bar, i) in bars"
        :key="bar.id"
        class="timeline-bar"
        :class="[bar.colorClass, { 'timeline-bar--open': bar.openEnded }]"
        :style="{
          left: bar.x + 'px',
          width: bar.w + 'px',
          top: (barRowHeight * i + 4) + 'px',
        }"
        :title="bar.tooltip"
      >
        <span class="timeline-bar__label">{{ bar.label }}</span>
        <span v-if="bar.openEnded" class="timeline-bar__open-badge" :title="$t('ui.noReturnDate')">?</span>
      </div>

      <!-- Empty state -->
      <div v-if="!bars.length" class="timeline-empty">{{ $t('items.noCheckoutHistory') }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const props = defineProps({
  /**
   * Array of ItemCheckoutDetail objects.
   * Each must have: id, checkout_date, effective_expected_checkin (nullable),
   *   actual_checkin (nullable), project (string or null), user (string or null).
   */
  checkouts: {
    type: Array,
    default: () => [],
  },
  /** How many days back to show (from today). */
  pastDays: {
    type: Number,
    default: 30,
  },
  /** How many days forward to show (from today). */
  futureDays: {
    type: Number,
    default: 90,
  },
  /** Pixels per day. */
  pxPerDay: {
    type: Number,
    default: 12,
  },
})

const containerEl = ref(null)
const barRowHeight = 28

const today = new Date()
today.setHours(0, 0, 0, 0)

const windowStart = computed(() => {
  const d = new Date(today)
  d.setDate(d.getDate() - props.pastDays)
  return d
})

const windowEnd = computed(() => {
  const d = new Date(today)
  d.setDate(d.getDate() + props.futureDays)
  return d
})

const totalDays = computed(() => props.pastDays + props.futureDays)
const totalWidth = computed(() => totalDays.value * props.pxPerDay)
const todayX = computed(() => props.pastDays * props.pxPerDay)

const trackHeight = computed(() => Math.max(48, bars.value.length * barRowHeight + 16))

// ── Month header labels ──────────────────────────────────────────────────────
const months = computed(() => {
  const result = []
  const cursor = new Date(windowStart.value)
  cursor.setDate(1)
  while (cursor <= windowEnd.value) {
    const monthStart = new Date(cursor)
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0)
    const clampedStart = monthStart < windowStart.value ? windowStart.value : monthStart
    const clampedEnd   = monthEnd   > windowEnd.value   ? windowEnd.value   : monthEnd

    const leftDays  = daysBetween(windowStart.value, clampedStart)
    const widthDays = daysBetween(clampedStart, clampedEnd) + 1

    result.push({
      label: cursor.toLocaleString(locale.value, { month: 'short', year: '2-digit' }),
      left:  leftDays  * props.pxPerDay,
      width: widthDays * props.pxPerDay,
    })
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return result
})

// ── Week guide marks ─────────────────────────────────────────────────────────
const weekGuides = computed(() => {
  const guides = []
  const cursor = new Date(windowStart.value)
  // Advance to next Monday
  const dow = cursor.getDay()
  const daysToMonday = dow === 1 ? 0 : (8 - dow) % 7
  cursor.setDate(cursor.getDate() + daysToMonday)
  while (cursor <= windowEnd.value) {
    guides.push(daysBetween(windowStart.value, cursor) * props.pxPerDay)
    cursor.setDate(cursor.getDate() + 7)
  }
  return guides
})

// ── Booking bars ─────────────────────────────────────────────────────────────
const OPEN_ENDED_OVERFLOW_DAYS = 30 // how far open-ended bars extend past window

const bars = computed(() => {
  const ws = windowStart.value
  const we = windowEnd.value

  return props.checkouts
    .filter((c) => {
      if (c.type === 'downtime') return false
      // Include if the checkout overlaps the window
      const start = c.start || c.checkout_date ? new Date(c.start ?? c.checkout_date) : null
      if (!start) return false
      const rawEnd = c.end ?? c.actual_checkin ?? c.effective_expected_checkin ?? c.expected_checkin
      const end = rawEnd ? new Date(rawEnd) : null
      // Open-ended: include if checkout_date <= windowEnd
      if (!end) return start <= we
      return start <= we && end >= ws
    })
    .map((c) => {
      const start = new Date(c.start ?? c.checkout_date)
      const isPlanning = c.type === 'planning'
      const isActive = isPlanning || !c.actual_checkin
      const expectedEnd = c.end ?? c.effective_expected_checkin ?? c.expected_checkin
      const openEnded = !isPlanning && isActive && !expectedEnd

      // End point: actual return, else expected, else extend past window
      let endDate
      if (c.actual_checkin || (c.end && !isPlanning)) {
        endDate = new Date(c.actual_checkin ?? c.end)
      } else if (expectedEnd) {
        endDate = new Date(expectedEnd)
      } else {
        // Open-ended: draw to windowEnd + overflow
        endDate = new Date(we)
        endDate.setDate(endDate.getDate() + OPEN_ENDED_OVERFLOW_DAYS)
      }

      const clampedStart = start < ws ? ws : start
      const clampedEnd   = endDate > we ? we : endDate

      const x = daysBetween(ws, clampedStart) * props.pxPerDay
      let w   = (daysBetween(clampedStart, clampedEnd) + 1) * props.pxPerDay

      // If open-ended, extend bar to full right edge of track
      if (openEnded) w = totalWidth.value - x + (OPEN_ENDED_OVERFLOW_DAYS * props.pxPerDay)

      const planningLabel = c.tentative ? t('itemMetadata.legendTentative') : t('itemMetadata.legendPlanned')
      const owner = c.project_name ?? c.project ?? c.user ?? t('common.unknown')
      const label = isPlanning
        ? `${c.asset_tag ? `${c.asset_tag} · ` : ''}${planningLabel}: ${owner}`
        : `${c.asset_tag ? `${c.asset_tag} · ` : ''}${owner}`
      const dateStr = `${fmtDate(start)}${expectedEnd ? ' → ' + fmtDate(new Date(expectedEnd)) : openEnded ? ' → ?' : ''}`
      const tooltip = `${label}\n${dateStr}`

      return {
        id: c.id ?? c.project_item_id ?? c.checkout_id ?? `${c.type}-${start.toISOString()}`,
        x: Math.max(0, x),
        w: Math.max(props.pxPerDay, Math.min(w, totalWidth.value + OPEN_ENDED_OVERFLOW_DAYS * props.pxPerDay - x)),
        openEnded,
        colorClass: isPlanning ? (c.tentative ? 'timeline-bar--tentative' : 'timeline-bar--planning') : openEnded ? 'timeline-bar--active-open' : isActive ? 'timeline-bar--active' : 'timeline-bar--returned',
        label,
        tooltip,
      }
    })
})

function daysBetween(a, b) {
  return Math.floor((b - a) / 86400000)
}

function fmtDate(d) {
  if (!d) return '?'
  const date = typeof d === 'string' ? new Date(d) : d
  return date.toLocaleDateString(locale.value, { month: 'short', day: 'numeric', year: '2-digit' })
}
</script>

<style scoped>
.item-timeline {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  background: var(--color-surface);
  border-radius: 0.75rem;
  border: 1px solid var(--color-border);
}

.timeline-months {
  position: relative;
  height: 22px;
  border-bottom: 1px solid var(--color-border);
  user-select: none;
}

.timeline-month-label {
  position: absolute;
  top: 3px;
  font-size: 0.68rem;
  color: var(--color-muted);
  letter-spacing: 0.03em;
  overflow: hidden;
  white-space: nowrap;
  padding-left: 4px;
  border-left: 1px solid var(--color-border);
}

.timeline-track {
  position: relative;
  padding: 4px 0 8px;
}

.timeline-today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(52, 211, 153, 0.6);
  z-index: 2;
}

.timeline-week-guide {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.04);
  z-index: 0;
}

.timeline-bar {
  position: absolute;
  height: 22px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 6px;
  font-size: 0.7rem;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  z-index: 1;
  max-width: 100%;
  cursor: default;
}

.timeline-bar--returned {
  background: rgba(107, 114, 128, 0.35);
  border: 1px solid rgba(107, 114, 128, 0.4);
  color: #9ca3af;
}

.timeline-bar--active {
  background: rgba(59, 130, 246, 0.35);
  border: 1px solid rgba(59, 130, 246, 0.5);
  color: #93c5fd;
}

.timeline-bar--active-open {
  background: rgba(217, 119, 6, 0.3);
  border: 1px solid rgba(217, 119, 6, 0.5);
  border-right: 2px dashed rgba(217, 119, 6, 0.6);
  color: #fbbf24;
}

.timeline-bar--planning {
  background: rgba(168, 85, 247, 0.22);
  border: 1px solid rgba(168, 85, 247, 0.42);
  color: #d8b4fe;
}

.timeline-bar--tentative {
  background: rgba(168, 85, 247, 0.12);
  border: 1px dashed rgba(168, 85, 247, 0.55);
  color: #d8b4fe;
}

.timeline-bar__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-bar__open-badge {
  flex-shrink: 0;
  margin-left: 4px;
  font-size: 0.68rem;
  opacity: 0.8;
}

.timeline-empty {
  padding: 16px;
  text-align: center;
  font-size: 0.8rem;
  color: var(--color-muted);
}
</style>
